import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState,
    type ReactNode,
} from 'react';
import { companyApi } from '@/api/company';
import { useAuth } from '@/providers/AuthProvider/AuthProvider';
import type { Company } from '@/types/types';

export interface CompanyContextValue {
    state: { company: Company };
    actions: {
        refreshCompany: () => Promise<void>;
        updateCompany: (payload: Partial<Company>) => Promise<void>;
        clearCompany: () => void;
    };
}

const CompanyContext = createContext<CompanyContextValue | null>(null);

export const CompanyProvider = ({ children }: { children: ReactNode }) => {
    const [company, setCompany] = useState<Company>({});
    const auth = useAuth();
    const loggedIn = auth.state.loggedIn;

    const refreshCompany = useCallback(async () => {
        try {
            const res = await companyApi.get();
            setCompany(res.data?.payload ?? {});
        } catch {}
    }, []);

    const updateCompany = useCallback(async (payload: Partial<Company>) => {
        try {
            const res = await companyApi.update(payload);
            setCompany(res.data?.payload ?? {});
        } catch {}
    }, []);

    const clearCompany = useCallback(() => setCompany({}), []);

    useEffect(() => {
        if (!loggedIn) setCompany({});
    }, [loggedIn]);

    return (
        <CompanyContext.Provider
            value={{
                state: { company },
                actions: { refreshCompany, updateCompany, clearCompany },
            }}
        >
            {children}
        </CompanyContext.Provider>
    );
};

export const useCompany = (): CompanyContextValue => {
    const ctx = useContext(CompanyContext);
    if (!ctx) throw new Error('useCompany must be used inside CompanyProvider');
    return ctx;
};
